<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
    <div v-if="isWixtarClient" class="alert alert-info wixtar-banner" role="status">
      <strong>Wixtar 客戶：</strong>點數與票券均以 Wixtar 為準，本頁直接向 Wixtar API 即時讀取。請先選擇會員後查詢。
    </div>

    <div v-if="isWixtarClient && dataSource === 'wixtar_mirror'" class="alert alert-warning wixtar-fallback-banner" role="alert">
      <strong>目前顯示的是 CRM 鏡像資料：</strong>向 Wixtar API 讀取點數流水失敗，已退回 Webhook 同步的鏡像流水。
      鏡像僅在 Wixtar 端有推送 Webhook 時才會寫入，內容可能不完整或落後，請確認 Wixtar 整合設定與 API 連線狀態。
    </div>

    <!-- 篩選工具列 -->
    <div class="page-toolbar">
      <form @submit.prevent="handleSearch" class="filter-form">
        <!-- 會員搜尋欄位 - 單獨一行 -->
        <div class="member-search-row">
          <MemberSearch
            v-model="selectedMembers"
            :max-selections="1"
            placeholder="輸入手機號碼或姓名搜尋會員"
            @selected="handleMemberSelected"
            @cleared="handleMemberCleared"
          />
        </div>

        <!-- 其他篩選條件 - 並排顯示 -->
        <div class="filter-row">
          <div v-if="!isWixtarClient" class="filter-group">
            <label>交易類型</label>
            <select v-model="filters.transactionType" class="form-control">
              <option value="">全部</option>
              <option value="earn">獲得</option>
              <option value="redeem">兌換</option>
              <option value="adjust">調整</option>
              <option value="expire">過期</option>
              <option value="refund">退款</option>
              <option value="sign_in">簽到</option>
            </select>
          </div>
          <div v-if="!isWixtarClient" class="filter-group">
            <label>狀態</label>
            <select v-model="filters.status" class="form-control">
              <option value="">全部</option>
              <option value="pending">待生效</option>
              <option value="active">可用</option>
              <option value="expired">已過期</option>
              <option value="used">已使用</option>
            </select>
          </div>
          <div class="filter-group">
            <label>開始日期</label>
            <input
              v-model="filters.startDate"
              type="date"
              class="form-control"
            />
          </div>
          <div class="filter-group">
            <label>結束日期</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="form-control"
            />
          </div>
          <div class="filter-actions">
            <button type="submit" class="btn btn-primary">
              <i class="ri-search-line"></i>
              搜尋
            </button>
            <button type="button" class="btn btn-secondary" @click="handleReset">
              重置
            </button>
          </div>
        </div>
      </form>
    </div>

    <div v-if="isWixtarClient && filters.memberId && wixtarTicketsLoading" class="loading-state">
      <div class="spinner"></div>
      <p>載入 Wixtar 票券中…</p>
    </div>
    <div v-else-if="isWixtarClient && filters.memberId && wixtarTickets.length" class="card wixtar-tickets-card">
      <div class="card-header">
        <h2 class="section-title">Wixtar 票券（API 即時）</h2>
      </div>
      <div class="card-body">
        <ul class="ticket-list">
          <li v-for="(t, idx) in wixtarTickets" :key="idx" class="ticket-item">
            <span class="ticket-title">{{ ticketLabel(t) }}</span>
            <span v-if="ticketStatus(t)" class="ticket-meta">{{ ticketStatus(t) }}</span>
          </li>
        </ul>
      </div>
    </div>
    <div v-else-if="isWixtarClient && filters.memberId && wixtarTicketsLoaded && !wixtarTickets.length" class="card wixtar-tickets-card">
      <div class="card-body text-muted">此會員目前無 Wixtar 票券資料，或 API 無法取得。</div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>交易時間</th>
            <th>會員</th>
            <th>交易類型</th>
            <th>點數</th>
            <!-- Wixtar API 不回傳異動後餘額與可用的訂單編號，對 Wixtar 客戶改顯示門市 -->
            <th v-if="!isWixtarClient">餘額</th>
            <th v-if="isWixtarClient">門市</th>
            <th>狀態</th>
            <th>說明</th>
            <th v-if="!isWixtarClient">關聯訂單</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in transactions" :key="item.id || item.wixtarEventId || `${item.createdAt}-${item.points}`">
            <td>{{ formatDate(item.createdAt) }}</td>
            <td>{{ item.memberName || item.memberId }}</td>
            <td>{{ formatTransactionType(item.transactionType) }}</td>
            <td :class="{ 'text-success': item.points > 0, 'text-danger': item.points < 0 }">
              {{ item.points > 0 ? '+' : '' }}{{ item.points }}
            </td>
            <td v-if="!isWixtarClient">{{ item.balance ?? '-' }}</td>
            <td v-if="isWixtarClient">{{ item.storeName || '-' }}</td>
            <td>
              <span :class="`badge badge-${getStatusClass(item.status)}`">
                {{ formatStatus(item.status) }}
              </span>
            </td>
            <td>{{ item.description || '-' }}</td>
            <td v-if="!isWixtarClient">
              <button
                v-if="item.orderId"
                class="btn-order-link"
                @click="goToOrder(item.orderId)"
              >
                <i class="ri-file-list-3-line"></i>
                查看訂單
              </button>
              <span v-else class="text-muted">-</span>
            </td>
          </tr>
          <tr v-if="transactions.length === 0">
            <td :colspan="isWixtarClient ? 7 : 8" class="empty-state">沒有資料</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分頁 -->
    <ListPagination
      v-if="pagination"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      :total="pagination.totalItems"
      @change="handlePageChange"
    />
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsService } from '@/api/modules/points.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showError } from '@/utils/swal.js';
import MemberSearch from '@/components/MemberSearch.vue';

export default {
  name: 'PointsTransactions',
  components: {
    MemberSearch
  },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('點數交易記錄');
    const loading = ref(false);
    const transactions = ref([]);
    const pagination = ref(null);
    const memberInfo = ref(null);
    const isWixtarClient = ref(false);
    // Wixtar 客戶的流水來源：'wixtar_api'（即時）或 'wixtar_mirror'（API 失敗退回鏡像）
    const dataSource = ref(null);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const wixtarTicketsLoaded = ref(false);
    const selectedMembers = ref([]);

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        if (res?.success && res.data?.clientData?.memberSystemType) {
          isWixtarClient.value = res.data.clientData.memberSystemType === 'WIXTAR';
        } else {
          isWixtarClient.value = false;
        }
      } catch (e) {
        console.error(e);
        isWixtarClient.value = false;
      }
    };

    const extractTicketsArray = (payload) => {
      const d = payload?.data ?? payload;
      if (Array.isArray(d)) return d;
      if (Array.isArray(d?.tickets)) return d.tickets;
      if (Array.isArray(d?.items)) return d.items;
      if (Array.isArray(d?.data)) return d.data;
      return [];
    };

    const loadWixtarTickets = async (memberId) => {
      if (!isWixtarClient.value || !memberId) {
        wixtarTickets.value = [];
        wixtarTicketsLoaded.value = false;
        return;
      }
      wixtarTicketsLoading.value = true;
      wixtarTicketsLoaded.value = false;
      wixtarTickets.value = [];
      try {
        const raw = await wixtarService.getMemberTickets(memberId, { identifierType: 'crm_member_id' });
        wixtarTickets.value = extractTicketsArray(raw);
      } catch (e) {
        console.error(e);
        wixtarTickets.value = [];
      } finally {
        wixtarTicketsLoading.value = false;
        wixtarTicketsLoaded.value = true;
      }
    };

    const ticketLabel = (t) => {
      if (!t || typeof t !== 'object') return String(t);
      return t.title || t.name || t.ticket_name || t.ticketName || t.activity_name || JSON.stringify(t).slice(0, 80);
    };

    const ticketStatus = (t) => {
      if (!t || typeof t !== 'object') return '';
      return t.status || t.state || t.ticket_status || '';
    };

    const filters = reactive({
      memberId: '',
      transactionType: '',
      status: '',
      startDate: '',
      endDate: '',
      page: 1,
      pageSize: 20
    });

    const handleMemberSelected = (member) => {
      if (member) {
        memberInfo.value = member;
        filters.memberId = member.id;
        loadWixtarTickets(member.id);
      } else {
        memberInfo.value = null;
        filters.memberId = '';
        wixtarTickets.value = [];
        wixtarTicketsLoaded.value = false;
      }
    };

    const handleMemberCleared = () => {
      selectedMembers.value = [];
      memberInfo.value = null;
      filters.memberId = '';
      wixtarTickets.value = [];
      wixtarTicketsLoaded.value = false;
    };

    const loadTransactions = async () => {
      // 檢查是否有任何搜尋條件
      const hasSearchCondition = 
        filters.memberId || 
        filters.transactionType || 
        filters.status || 
        filters.startDate || 
        filters.endDate;

      // 如果沒有任何搜尋條件，不執行查詢
      if (!hasSearchCondition) {
        transactions.value = [];
        pagination.value = null;
        dataSource.value = null;
        return;
      }

      if (isWixtarClient.value && !filters.memberId) {
        showError('請先選擇會員', 'Wixtar 客戶需指定會員後，才能向 Wixtar 讀取點數流水。');
        transactions.value = [];
        pagination.value = null;
        dataSource.value = null;
        return;
      }

      loading.value = true;
      try {
        const response = await pointsService.listAllTransactions(filters);
        if (response.success && response.data) {
          transactions.value = response.data.items || [];
          pagination.value = response.data.pagination;
          dataSource.value = response.data.dataSource || null;
        }
      } catch (error) {
        showError('載入交易記錄失敗', error.message);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      filters.page = 1;
      loadTransactions();
    };

    const handleReset = () => {
      selectedMembers.value = [];
      filters.memberId = '';
      filters.transactionType = '';
      filters.status = '';
      filters.startDate = '';
      filters.endDate = '';
      filters.page = 1;
      memberInfo.value = null;
      dataSource.value = null;
      wixtarTickets.value = [];
      wixtarTicketsLoaded.value = false;
      loadTransactions();
    };

    const handlePageChange = (page) => {
      filters.page = page;
      loadTransactions();
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW');
    };

    const formatTransactionType = (type) => {
      const types = {
        earn: '獲得',
        redeem: '兌換',
        adjust: '調整',
        expire: '過期',
        refund: '退款',
        sign_in: '簽到',
        wixtar_mirror: 'Wixtar 鏡像'
      };
      return types[type] || type;
    };

    const formatStatus = (status) => {
      const statuses = {
        pending: '待生效',
        active: '可用',
        expired: '已過期',
        used: '已使用'
      };
      return statuses[status] || status;
    };

    const getStatusClass = (status) => {
      const classes = {
        pending: 'warning',
        active: 'success',
        expired: 'secondary',
        used: 'info'
      };
      return classes[status] || 'secondary';
    };

    onMounted(async () => {
      await loadClientMemberSystem();
      transactions.value = [];
      pagination.value = null;
    });

    watch(
      () => filters.memberId,
      (id) => {
        if (isWixtarClient.value && id) {
          loadWixtarTickets(id);
        }
      }
    );

    const goToOrder = (orderId) => {
      router.push({ name: 'ClientOrderDetail', params: { id: orderId } });
    };

    return {
      pageTitle,
      loading,
      transactions,
      pagination,
      filters,
      memberInfo,
      selectedMembers,
      handleMemberSelected,
      handleMemberCleared,
      handleSearch,
      handleReset,
      handlePageChange,
      formatDate,
      formatTransactionType,
      formatStatus,
      getStatusClass,
      goToOrder,
      isWixtarClient,
      dataSource,
      wixtarTickets,
      wixtarTicketsLoading,
      wixtarTicketsLoaded,
      ticketLabel,
      ticketStatus
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* 篩選表單樣式 */
.filter-form {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
}

/* 會員搜尋欄位 - 單獨一行 */
.member-search-row {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.filter-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
}

.filter-group {
  flex: 1;
  min-width: 150px;
}

.filter-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  margin-bottom: 6px;
}

.filter-actions {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

/* 文字顏色 */
.text-success {
  color: #28a745;
  font-weight: 500;
}

.text-danger {
  color: #dc3545;
  font-weight: 500;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
  }

  .filter-group {
    min-width: 100%;
  }

  .filter-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

.btn-order-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.btn-order-link:hover {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}

.btn-order-link i {
  font-size: 13px;
}

.text-muted {
  color: #9ca3af;
}

/* 專案未引入 Bootstrap，.alert / .alert-info 無全域樣式，配色在此自行定義 */
.wixtar-banner,
.wixtar-fallback-banner {
  margin: 0 0 16px;
  padding: 12px 16px;
  border: 1px solid transparent;
  border-left-width: 4px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.7;
}

.wixtar-banner {
  border-color: #bfdbfe;
  border-left-color: #2563eb;
  background: #eff6ff;
  color: #1e3a5f;
}

.wixtar-fallback-banner {
  border-color: #fde68a;
  border-left-color: #d97706;
  background: #fffbeb;
  color: #713f12;
}

.wixtar-tickets-card {
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.wixtar-tickets-card .card-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.wixtar-tickets-card .card-body {
  padding: 12px 16px;
}

.ticket-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ticket-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.ticket-item:last-child {
  border-bottom: none;
}

.ticket-meta {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
}
</style>
